import type { ReactNode } from 'react'
import { Tooltip } from './Tooltip'

const WEEKDAY_HEADERS_MON: ReadonlyArray<string> = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const WEEKDAY_HEADERS_SUN: ReadonlyArray<string> = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export interface MonthGridCell {
  /** ISO date for in-month cells; null for leading/trailing blanks. */
  date: string | null
}

export interface MonthGridDayMeta {
  /** Marks a day as a public holiday — tints the cell + shows label. */
  publicHoliday?: string
  /** Marks a day as today. Alternative to passing `today` ISO on the grid. */
  isToday?: boolean
  /** Coverage stress on this day — renders a 2px ribbon at cell top. */
  coverageWarning?: 'medium' | 'high'
  /** Marks a day inside a blackout window — fills the cell with a hatch. */
  blackout?: boolean
}

export interface MonthGridProps {
  /** Pre-built cells. OR pass `month` and the grid builds them internally. */
  cells?: ReadonlyArray<MonthGridCell>
  /** Convenience: builds cells internally. Format "YYYY-MM". */
  month?: string
  /** ISO date for today. Defaults to current date when omitted. */
  today?: string
  /** Per-day metadata keyed by ISO date string. */
  dayMeta?: ReadonlyMap<string, MonthGridDayMeta>
  /** Render a cell's body. Default: day number + today highlight ring. */
  renderCell?: (args: {
    date: string | null
    day: number | null
    isToday: boolean
    meta?: MonthGridDayMeta
  }) => ReactNode
  /**
   * Render an absolutely-positioned overlay on top of each week row.
   * The host `div` is `position: relative`. Use for Gantt-style bars.
   */
  renderWeekOverlay?: (args: {
    weekCells: ReadonlyArray<MonthGridCell>
    weekIndex: number
  }) => ReactNode
  /** Cell min-height in px. Default 80. */
  cellMinHeight?: number
  /** Optional click handler — fires when a non-blank cell is clicked. */
  onCellClick?: (date: string, anchor: HTMLElement) => void
  /**
   * Which day the week column starts on. Default `monday`. Only affects the
   * weekday header labels + weekend shading — the caller's `cells` must already
   * be laid out to match.
   */
  weekStartsOn?: 'monday' | 'sunday'
  'data-testid'?: string
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * Build a Monday-first array of MonthGridCells for the given "YYYY-MM".
 * Leading/trailing blanks have `date: null`. Length is always a multiple of 7.
 */
export function buildMonthCells(month: string): MonthGridCell[] {
  const [yearStr, monthStr] = month.split('-')
  const year = Number(yearStr)
  const monthIndex = Number(monthStr) - 1
  const firstDay = new Date(year, monthIndex, 1)
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate()
  // Monday-first offset: Sun=6, Mon=0, Tue=1, ...
  const rawDow = firstDay.getDay()
  const leadingBlanks = rawDow === 0 ? 6 : rawDow - 1
  const cells: MonthGridCell[] = []
  for (let i = 0; i < leadingBlanks; i++) cells.push({ date: null })
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: `${year}-${pad2(monthIndex + 1)}-${pad2(d)}` })
  }
  while (cells.length % 7 !== 0) cells.push({ date: null })
  return cells
}

function DefaultDayNumber({ day, isToday }: { day: number | null; isToday: boolean }): JSX.Element {
  if (day === null) return <></>
  return (
    <span
      style={{
        fontFamily: 'var(--font-mono, inherit)',
        fontSize: 12,
        fontWeight: isToday ? 700 : 500,
        color: isToday ? 'var(--primary-fg)' : 'var(--text)',
        background: isToday ? 'var(--primary-bg-2)' : 'transparent',
        borderRadius: '50%',
        width: 24,
        height: 24,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        textAlign: 'center',
      }}
    >
      {day}
    </span>
  )
}

/**
 * MonthGrid — reusable 7-column Monday-first month-grid layout primitive.
 * Provides the outer skeleton without domain-specific bar packing.
 * Compose content via `renderCell` and `renderWeekOverlay` slots.
 */
export function MonthGrid({
  cells: cellsProp,
  month,
  today,
  dayMeta,
  renderCell,
  renderWeekOverlay,
  cellMinHeight = 80,
  onCellClick,
  weekStartsOn = 'monday',
  'data-testid': testId = 'month-grid',
}: MonthGridProps): JSX.Element {
  const weekdayHeaders =
    weekStartsOn === 'sunday' ? WEEKDAY_HEADERS_SUN : WEEKDAY_HEADERS_MON
  // Resolve today ISO — default to system date
  const todayIso =
    today ?? new Date().toISOString().slice(0, 10)

  // Resolve cells — either from prop or build internally from `month`
  const cells: ReadonlyArray<MonthGridCell> = (() => {
    if (cellsProp !== undefined) return cellsProp
    if (month !== undefined) return buildMonthCells(month)
    return []
  })()

  // Partition into weeks
  const weeks: MonthGridCell[][] = []
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7) as MonthGridCell[])
  }

  return (
    <div
      data-testid={testId}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        background: 'var(--surface)',
        borderRadius: 12,
        border: '1px solid var(--line-strong)',
        boxShadow: 'var(--shadow-1)',
        overflow: 'hidden',
      }}
    >
      <style>{`
  @media (max-width: 719px) {
    [data-testid="${testId}"] {
      grid-template-columns: repeat(7, minmax(40px, 1fr)) !important;
    }
    [data-testid="${testId}"] [data-role="month-grid-cell"] {
      min-height: 56px !important;
      padding: 3px 4px !important;
    }
    [data-testid="${testId}"] [data-testid="public-holiday"] {
      display: none !important;
    }
  }
  @media (max-width: 479px) {
    [data-testid="${testId}"] {
      grid-template-columns: repeat(7, minmax(32px, 1fr)) !important;
    }
    [data-testid="${testId}"] [data-role="month-grid-cell"] {
      min-height: 44px !important;
      padding: 2px !important;
    }
  }
`}</style>

      {/* Weekday header row */}
      {weekdayHeaders.map((label) => (
        <div
          key={label}
          style={{
            padding: '15px 12px',
            textAlign: 'left',
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--text-3)',
            background: 'var(--weekend-bg)',
            borderBottom: '1px solid var(--line-strong)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontFamily: 'var(--font-mono, inherit)',
          }}
        >
          {label}
        </div>
      ))}

      {/* Week rows */}
      {weeks.map((weekCells, weekIndex) => (
        <MonthGridWeekRow
          key={weekIndex}
          cells={weekCells}
          weekIndex={weekIndex}
          todayIso={todayIso}
          dayMeta={dayMeta}
          renderCell={renderCell}
          renderWeekOverlay={renderWeekOverlay}
          cellMinHeight={cellMinHeight}
          onCellClick={onCellClick}
          weekStartsOn={weekStartsOn}
        />
      ))}
    </div>
  )
}

interface MonthGridWeekRowProps {
  cells: ReadonlyArray<MonthGridCell>
  weekIndex: number
  todayIso: string
  dayMeta: ReadonlyMap<string, MonthGridDayMeta> | undefined
  renderCell: MonthGridProps['renderCell']
  renderWeekOverlay: MonthGridProps['renderWeekOverlay']
  cellMinHeight: number
  onCellClick: MonthGridProps['onCellClick']
  weekStartsOn: 'monday' | 'sunday'
}

function MonthGridWeekRow({
  cells,
  weekIndex,
  todayIso,
  dayMeta,
  renderCell,
  renderWeekOverlay,
  cellMinHeight,
  onCellClick,
  weekStartsOn,
}: MonthGridWeekRowProps): JSX.Element {
  return (
    <div
      style={{
        gridColumn: '1 / -1',
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        position: 'relative',
        // Clip absolutely-positioned overlays (e.g. WeekBarOverlay) so a
        // tall bar stack can't visually bleed into the next week's cells.
        // Tooltips render via createPortal so they aren't affected.
        overflow: 'hidden',
      }}
    >
      {cells.map((cell, colIndex) => {
        // Sunday-first: Sun (col 0) + Sat (col 6). Monday-first: Sat/Sun (cols 5/6).
        const isWeekend =
          weekStartsOn === 'sunday'
            ? colIndex === 0 || colIndex === 6
            : colIndex === 5 || colIndex === 6
        const isBlank = cell.date === null

        const meta = cell.date !== null ? dayMeta?.get(cell.date) : undefined
        const phName = meta?.publicHoliday
        const isToday =
          cell.date !== null &&
          (meta?.isToday === true || cell.date === todayIso)

        const day = cell.date !== null ? Number(cell.date.slice(8)) : null

        const borderRight: React.CSSProperties =
          colIndex < 6
            ? { borderRight: '1px solid var(--line-strong)' }
            : {}
        const borderBottom: React.CSSProperties = {
          borderBottom: '1px solid var(--line-strong)',
        }

        if (isBlank) {
          return (
            <div
              key={`blank-${weekIndex}-${colIndex}`}
              data-role="month-grid-cell"
              style={{
                minHeight: cellMinHeight,
                background: 'var(--weekend-bg)',
                ...borderRight,
                ...borderBottom,
              }}
            />
          )
        }

        // Public holidays get a very pale red wash (plus the bright-red date +
        // label) so holiday columns are scannable at a glance. The holiday tint
        // wins over the weekend grey — a holiday is the more meaningful flag.
        const cellBg = phName
          ? 'var(--danger-bg-3)'
          : isWeekend
            ? 'var(--weekend-bg)'
            : 'transparent'

        // Blackout days are marked with red diagonal hatch lines only (no fill)
        // sitting BEHIND the day number (the number stays crisp). The underlying
        // weekend/holiday tint shows through. A red pill naming the window is
        // layered in the week overlay by the calendar.
        const isBlackout = meta?.blackout === true
        const cellBackground: React.CSSProperties = isBlackout
          ? {
              backgroundColor: cellBg,
              // Feathered (anti-aliased) diagonal lines — soft edges make every
              // stripe rasterize to the same weight, avoiding the thick/thin
              // aliasing that hard 1px stops produce at 45deg.
              backgroundImage:
                'repeating-linear-gradient(45deg, transparent 0 6px, var(--blackout-hatch) 7px, transparent 8px 13px)',
            }
          : { background: cellBg }

        const handleCellClick =
          onCellClick !== undefined && cell.date !== null
            ? (e: React.MouseEvent<HTMLDivElement>) =>
                onCellClick(cell.date!, e.currentTarget)
            : undefined

        return (
          <div
            key={cell.date}
            data-role="month-grid-cell"
            data-date={cell.date}
            onClick={handleCellClick}
            style={{
              minHeight: cellMinHeight,
              minWidth: 0,
              padding: '6px 8px',
              ...cellBackground,
              position: 'relative',
              cursor: handleCellClick !== undefined ? 'pointer' : undefined,
              ...borderRight,
              ...borderBottom,
            }}
          >
            {/* Coverage ribbon — 2px stripe at top edge */}
            {meta?.coverageWarning !== undefined ? (
              <div
                data-testid="coverage-ribbon"
                data-level={meta.coverageWarning}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 2,
                  background:
                    meta.coverageWarning === 'high' ? 'var(--danger)' : 'var(--warning)',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              />
            ) : null}

            {/* Day header area: number + optional holiday label */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 4,
                marginBottom: 4,
                marginTop: 0,
                minWidth: 0,
              }}
            >
              {/* Day number — either custom renderer or built-in */}
              {renderCell !== undefined ? (
                renderCell({
                  date: cell.date,
                  day,
                  isToday,
                  ...(meta !== undefined ? { meta } : {}),
                })
              ) : (
                <DefaultDayNumber day={day} isToday={isToday} />
              )}

              {/* Public-holiday label — styled hover tooltip (same as leave bars) */}
              {phName !== undefined ? (
                <Tooltip content={phName} placement="top">
                  <span
                    data-testid="public-holiday"
                    style={{
                      fontFamily: 'var(--font-mono, inherit)',
                      fontSize: 10,
                      fontWeight: 600,
                      color: 'var(--danger)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 'calc(100% - 30px)',
                      whiteSpace: 'nowrap',
                      flexShrink: 1,
                      minWidth: 0,
                    }}
                  >
                    {phName}
                  </span>
                </Tooltip>
              ) : null}
            </div>
          </div>
        )
      })}

      {/* Week overlay slot — caller positions bars absolutely here */}
      {renderWeekOverlay !== undefined
        ? renderWeekOverlay({ weekCells: cells, weekIndex })
        : null}
    </div>
  )
}
